import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { sumRateCardsDeliveryPercentage } from '../sumRateCardsDeliveryPercentage';
import { v4 as uuidv4 } from 'uuid';
import { useGetTradingDealChannelsQuery } from '@/api';
import { resolveEffectiveTradingDealPercent } from '@/utils/resolveEffectiveTradingDealPercent';
import {
  CalculatorSettings,
  RateCard,
  KPIMetrics,
  UseEU6Calculator,
  Currency,
  BuyingPoint,
  FXRates,
  RateCardInput,
  StoredRateCard
} from '../EU6Calculator.types';

const DEFAULT_SETTINGS: CalculatorSettings = {
  totalBudget: 100000,
  sellCPM: 5,
  displayCurrency: Currency.GBP,
  buyingPoint: BuyingPoint.PROGRAMMATIC,
  tradingDealName: '',
  tradingDealId: null,
  tradingDealPercentage: 0,
  hardCostsTotal: 0
};

// Default FX rates (base is GBP)
const DEFAULT_FX_RATES: FXRates = {
  [Currency.GBP]: 1.0,
  [Currency.EUR]: 0.87,
  [Currency.USD]: 0.75,
  [Currency.CAD]: 0.55
};

function toStoredRateCards(inputs: RateCardInput[]): StoredRateCard[] {
  return inputs.map((input) => ({ id: uuidv4(), ...input }));
}

export interface UseEU6CalculatorOptions {
  /** Preload rate cards (e.g. when editing a saved package) */
  initialRateCards?: RateCardInput[];
  /** Preload calculator settings (duplicate / edit package). Applied once on mount. */
  initialSettings?: Partial<CalculatorSettings>;
  /** Preload FX rates from a saved package. Applied once on mount. */
  initialFxRates?: Partial<FXRates>;
  /** When this value changes, settings / FX / rows are reloaded from the options above. */
  resetSeed?: string;
}

export const useEU6Calculator = (options?: UseEU6CalculatorOptions): UseEU6Calculator => {
  const { data: tradingDealChannelsData } = useGetTradingDealChannelsQuery();
  // Memoised so the empty fallback keeps one identity for the hooks that depend on it.
  const tradingDealChannels = useMemo(
    () => tradingDealChannelsData?.tradingDealChannels ?? [],
    [tradingDealChannelsData]
  );

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const lastResetSeedRef = useRef<string | undefined>(undefined);

  const [settings, setSettings] = useState<CalculatorSettings>(() => ({
    ...DEFAULT_SETTINGS,
    ...(options?.initialSettings ?? {})
  }));
  const [storedRateCards, setStoredRateCards] = useState<StoredRateCard[]>(() =>
    options?.initialRateCards?.length ? toStoredRateCards(options.initialRateCards) : []
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fxRates, setFxRates] = useState<FXRates>(() => ({
    ...DEFAULT_FX_RATES,
    ...(options?.initialFxRates ?? {})
  }));

  useEffect(() => {
    const seed = options?.resetSeed;
    if (seed == null || seed === lastResetSeedRef.current) return;
    lastResetSeedRef.current = seed;

    const opts = optionsRef.current;
    setSettings({ ...DEFAULT_SETTINGS, ...(opts?.initialSettings ?? {}) });
    setFxRates({ ...DEFAULT_FX_RATES, ...(opts?.initialFxRates ?? {}) });
    setStoredRateCards(opts?.initialRateCards?.length ? toStoredRateCards(opts.initialRateCards) : []);
  }, [options?.resetSeed]);

  // FX conversion helper
  const convertCurrency = useCallback(
    (amount: number, fromCurrency: Currency, toCurrency: Currency): number => {
      if (fromCurrency === toCurrency) return amount;
      // Convert to base currency (GBP) then to target currency
      const inBase = amount / fxRates[fromCurrency];
      return inBase * fxRates[toCurrency];
    },
    [fxRates]
  );

  // Calculate individual rate card metrics with proper formulas
  const calculateRateCardMetrics = useCallback(
    (
      inputCard: Omit<
        RateCard,
        | 'id'
        | keyof Omit<
            RateCard,
            | 'id'
            | 'country'
            | 'channel'
            | 'publisher'
            | 'format'
            | 'deliveryPercentage'
            | 'rowCurrency'
            | 'buyCPM'
            | 'dspFeeId'
            | 'dspFeeName'
            | 'dspFeePercentage'
          >
      >,
      totalNetNative: number
    ): RateCard => {
      const deliveryFraction = inputCard.deliveryPercentage / 100;

      // Allocate in plan currency first so row gross always tracks Total Budget × % delivery
      const grossBudgetPlan = settings.totalBudget * deliveryFraction;
      const grossBudgetNative = convertCurrency(
        grossBudgetPlan,
        settings.displayCurrency,
        inputCard.rowCurrency
      );

      // Net Budget (native) = Gross Budget × 0.85 (fixed 15% discount)
      const netBudgetNative = grossBudgetNative * 0.85;

      // Impressions = (Gross Budget ÷ Sell CPM) × 1000
      const impressions = (grossBudgetNative / settings.sellCPM) * 1000;

      // Publisher Spend (native) = (Impressions ÷ 1000) × Buy CPM
      const publisherSpendNative = (impressions / 1000) * inputCard.buyCPM;

      // DSP Fee Value (native) = (Buy CPM × DSP Fee %) × (Impressions ÷ 1000)
      const dspFeeValueNative = inputCard.buyCPM * (inputCard.dspFeePercentage / 100) * (impressions / 1000);

      // Total Media Spend (native) = Publisher Spend + DSP Fee Value
      const totalMediaSpendNative = publisherSpendNative + dspFeeValueNative;

      // Hard cost allocation
      // Row Share of Net = Row Net Budget ÷ Total Net
      const rowShareOfNet = totalNetNative > 0 ? netBudgetNative / totalNetNative : 0;

      // Allocated Hard Cost (plan currency) = Row Share × Hard Costs Total
      const allocatedHardCostPlan = rowShareOfNet * settings.hardCostsTotal;

      // Allocated Hard Cost (native) = FX-converted to row currency
      const allocatedHardCostNative = convertCurrency(
        allocatedHardCostPlan,
        settings.displayCurrency,
        inputCard.rowCurrency
      );

      // Gross Margin (native, before rebate) = Net Budget − Total Media Spend − Allocated Hard Cost
      const grossMarginNative = netBudgetNative - totalMediaSpendNative - allocatedHardCostNative;

      const effectiveTradingDealPercentage = resolveEffectiveTradingDealPercent(
        settings.tradingDealName,
        inputCard.channel,
        settings.tradingDealPercentage,
        tradingDealChannels
      );

      // Rebate (native) = Net Budget × effective Trading Deal %
      const tradingDealAmountNative = netBudgetNative * (effectiveTradingDealPercentage / 100);

      // Gross Profit (native, after rebate) = Gross Margin − Rebate
      const grossProfitNative = grossMarginNative - tradingDealAmountNative;

      // Gross Margin % (before rebate) = Gross Margin ÷ Net Budget
      const marginPercentage = netBudgetNative !== 0 ? (grossMarginNative / netBudgetNative) * 100 : 0;

      // Gross Profit % (after rebate) = Gross Profit ÷ Net Budget
      const profitPercentage = netBudgetNative !== 0 ? (grossProfitNative / netBudgetNative) * 100 : 0;

      const netBudgetPlan = convertCurrency(netBudgetNative, inputCard.rowCurrency, settings.displayCurrency);
      const publisherSpendPlan = convertCurrency(
        publisherSpendNative,
        inputCard.rowCurrency,
        settings.displayCurrency
      );
      const dspFeeValuePlan = convertCurrency(
        dspFeeValueNative,
        inputCard.rowCurrency,
        settings.displayCurrency
      );
      const totalMediaSpendPlan = convertCurrency(
        totalMediaSpendNative,
        inputCard.rowCurrency,
        settings.displayCurrency
      );
      const grossMarginPlan = convertCurrency(
        grossMarginNative,
        inputCard.rowCurrency,
        settings.displayCurrency
      );
      const tradingDealAmountPlan = convertCurrency(
        tradingDealAmountNative,
        inputCard.rowCurrency,
        settings.displayCurrency
      );
      const grossProfitPlan = convertCurrency(
        grossProfitNative,
        inputCard.rowCurrency,
        settings.displayCurrency
      );

      return {
        id: uuidv4(),
        country: inputCard.country,
        channel: inputCard.channel,
        publisher: inputCard.publisher,
        format: inputCard.format,
        deliveryPercentage: inputCard.deliveryPercentage,
        rowCurrency: inputCard.rowCurrency,
        buyCPM: inputCard.buyCPM,
        dspFeeId: inputCard.dspFeeId,
        dspFeeName: inputCard.dspFeeName,
        dspFeePercentage: inputCard.dspFeePercentage,
        grossBudgetNative,
        netBudgetNative,
        impressions,
        publisherSpendNative,
        dspFeeValueNative,
        totalMediaSpendNative,
        allocatedHardCostNative,
        grossMarginNative,
        tradingDealAmountNative,
        grossProfitNative,
        effectiveTradingDealPercentage,
        marginPercentage,
        profitPercentage,
        grossBudgetPlan,
        netBudgetPlan,
        publisherSpendPlan,
        dspFeeValuePlan,
        totalMediaSpendPlan,
        allocatedHardCostPlan,
        grossMarginPlan,
        tradingDealAmountPlan,
        grossProfitPlan
      };
    },
    [
      settings.totalBudget,
      settings.sellCPM,
      settings.displayCurrency,
      settings.hardCostsTotal,
      settings.tradingDealPercentage,
      settings.tradingDealName,
      tradingDealChannels,
      convertCurrency
    ]
  );

  // Recalculate all rate cards when settings or FX rates change
  const recalculatedRateCards = useMemo(() => {
    if (storedRateCards.length === 0) return [];

    // First pass: calculate total net (native) for hard cost allocation
    let totalNetNative = 0;
    storedRateCards.forEach((card) => {
      const deliveryFraction = card.deliveryPercentage / 100;
      const grossBudgetPlan = settings.totalBudget * deliveryFraction;
      const grossBudgetNative = convertCurrency(
        grossBudgetPlan,
        settings.displayCurrency,
        card.rowCurrency
      );
      const netBudgetNative = grossBudgetNative * 0.85;
      totalNetNative += netBudgetNative;
    });

    // Second pass: derive all plan columns from total budget × % delivery
    return storedRateCards.map((card) => {
      const inputCard: RateCardInput = {
        country: card.country,
        channel: card.channel,
        publisher: card.publisher,
        format: card.format,
        deliveryPercentage: card.deliveryPercentage,
        rowCurrency: card.rowCurrency,
        buyCPM: card.buyCPM,
        dspFeeId: card.dspFeeId,
        dspFeeName: card.dspFeeName,
        dspFeePercentage: card.dspFeePercentage
      };
      const recalculated = calculateRateCardMetrics(inputCard, totalNetNative);
      return { ...recalculated, id: card.id };
    });
  }, [
    storedRateCards,
    settings.totalBudget,
    settings.sellCPM,
    settings.displayCurrency,
    settings.hardCostsTotal,
    settings.tradingDealPercentage,
    settings.tradingDealName,
    tradingDealChannels,
    convertCurrency,
    calculateRateCardMetrics
  ]);

  // Calculate KPI metrics
  const kpiMetrics = useMemo((): KPIMetrics => {
    const totalGrossBudgetPlan = recalculatedRateCards.reduce((sum, card) => sum + card.grossBudgetPlan, 0);
    const totalNetBudgetPlan = recalculatedRateCards.reduce((sum, card) => sum + card.netBudgetPlan, 0);
    const totalGrossMarginPlan = recalculatedRateCards.reduce((sum, card) => sum + card.grossMarginPlan, 0);
    const totalGrossProfitPlan = recalculatedRateCards.reduce((sum, card) => sum + card.grossProfitPlan, 0);

    // Blended Margin % = (Sum of Gross Margin in plan currency) ÷ (Sum of Net Budget in plan currency)
    const blendedMarginPercentage =
      totalNetBudgetPlan > 0 ? (totalGrossMarginPlan / totalNetBudgetPlan) * 100 : 0;

    // Blended Profit % = (Sum of Gross Profit in plan currency) ÷ (Sum of Net Budget in plan currency)
    const blendedProfitPercentage =
      totalNetBudgetPlan > 0 ? (totalGrossProfitPlan / totalNetBudgetPlan) * 100 : 0;

    return {
      sellCPM: settings.sellCPM,
      totalDeliveryPercentage: sumRateCardsDeliveryPercentage(storedRateCards),
      totalGrossBudgetPlan,
      totalNetBudgetPlan,
      blendedMarginPercentage,
      blendedProfitPercentage,
      totalGrossMarginPlan,
      totalGrossProfitPlan
    };
  }, [recalculatedRateCards, storedRateCards, settings.sellCPM]);

  const updateSettings = useCallback((key: keyof CalculatorSettings, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateFXRates = useCallback((rates: Partial<FXRates>) => {
    setFxRates((prev) => ({ ...prev, ...rates }));
  }, []);

  const addRateCard = useCallback((inputCard: RateCardInput) => {
    setStoredRateCards((prev) => [...prev, { id: uuidv4(), ...inputCard }]);
  }, []);

  const updateRateCard = useCallback((id: string, updates: Partial<RateCardInput>) => {
    setStoredRateCards((prev) =>
      prev.map((card) => (card.id === id ? { ...card, ...updates } : card))
    );
  }, []);

  const deleteRateCard = useCallback((id: string) => {
    setStoredRateCards((prev) => prev.filter((card) => card.id !== id));
  }, []);

  const duplicateRateCard = useCallback(
    (card: RateCard) => {
      const input: RateCardInput = {
        country: card.country,
        channel: card.channel,
        publisher: card.publisher,
        format: card.format,
        deliveryPercentage: card.deliveryPercentage,
        rowCurrency: card.rowCurrency,
        buyCPM: card.buyCPM,
        dspFeeId: card.dspFeeId,
        dspFeeName: card.dspFeeName,
        dspFeePercentage: card.dspFeePercentage
      };
      addRateCard(input);
    },
    [addRateCard]
  );

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return {
    settings,
    rateCards: recalculatedRateCards,
    kpiMetrics,
    isModalOpen,
    fxRates,
    updateSettings,
    updateFXRates,
    addRateCard,
    duplicateRateCard,
    updateRateCard,
    deleteRateCard,
    openModal,
    closeModal
  };
};
